import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  PageTitle,
  LoadingSpinner,
  TruncatedCell,
  OrderConfirmationModal,
} from "../../common";
import { useApiRequests } from "../../services";
import { CartContext } from "../../CartContext";
import { toastManager } from "../../services/toastManager";
import "./styles.scss";

export const Cart = () => {
  const { cart, refreshCart } = useContext(CartContext);
  const { removeFromCart, checkoutCart } = useApiRequests();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    let mounted = true;
    (async () => {
      await refreshCart();
      if (mounted) {
        setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [refreshCart]);

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
    } catch (error) {
      // the axios interceptor already surfaces the error toast
    } finally {
      await refreshCart();
    }
  };

  const handleSendOrder = async () => {
    setIsCheckingOut(true);
    try {
      await checkoutCart();
      await refreshCart();
      setIsConfirmOpen(false);
      if (userType === "1") {
        toastManager.success("Purchase completed.");
        setIsCheckingOut(false);
      } else {
        toastManager.success("Order sent for approval.");
        history.push("/my-requests");
      }
    } catch (error) {
      // interceptor surfaces the error toast (e.g. "Not enough balance")
      setIsConfirmOpen(false);
      setIsCheckingOut(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  const items = cart.items || [];
  const overBalance = cart.total > cart.availableBalance;
  const hasUnavailable = items.some((item) => !item.isAvailable);
  const checkoutDisabled =
    items.length === 0 || overBalance || hasUnavailable || isCheckingOut;

  return (
    <>
      <PageTitle title="Your Cart" />
      <div className="col-8 m-auto cart-page">
        {items.length === 0 ? (
          <p className="cart-empty">Your cart is empty.</p>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Comment</th>
                  <th>Price (kudos)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.productId}>
                    <td>
                      {item.productName}
                      {!item.isAvailable && (
                        <span className="cart-unavailable-tag">
                          {" "}
                          (unavailable)
                        </span>
                      )}
                    </td>
                    <td className="cart-comment-cell">
                      {item.comment ? (
                        <TruncatedCell text={item.comment} />
                      ) : (
                        <span className="cart-no-comment">—</span>
                      )}
                    </td>
                    <td>{item.productPrice}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={() => handleRemove(item.productId)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="cart-summary">
              <p>
                <b>Total:</b> {cart.total} kudos
              </p>
              <p>
                <b>Available balance:</b> {cart.availableBalance} kudos
              </p>
              {overBalance && (
                <p className="cart-error">
                  Cart total exceeds your available balance.
                </p>
              )}
              {hasUnavailable && (
                <p className="cart-error">
                  Some items are no longer available. Remove them to send the
                  order.
                </p>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={checkoutDisabled}
              onClick={() => setIsConfirmOpen(true)}
            >
              {isCheckingOut ? "Sending..." : "Send order"}
            </button>

            <OrderConfirmationModal
              isOpen={isConfirmOpen}
              itemCount={items.length}
              total={cart.total}
              isSending={isCheckingOut}
              onConfirm={handleSendOrder}
              onCancel={() => setIsConfirmOpen(false)}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Cart;
